import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import {
  mdiContentCopy,
  mdiEmailOutline,
  mdiExportVariant,
  mdiFacebook,
  mdiLinkedin,
} from '@mdi/js';
import Icon from '@mdi/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// X (Twitter) logo SVG path
const xLogoPath =
  'M16.99 0H20.298L13.071 8.26L21.573 19.5H14.916L9.702 12.683L3.736 19.5H0.426L8.156 10.665L0 0H6.826L11.539 6.231L16.99 0ZM15.829 17.52H17.662L5.83 1.876H3.863L15.829 17.52Z';

const shareButtonStyle = css`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
`;

const TrianglePointer = styled.span`
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
`;

const SharePopover = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  padding: 1rem 0;
  background: white;
  border-radius: 8px;
  border: 1px solid rgb(213 213 213);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  width: 18rem;
`;

const ShareOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.25rem 2rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  span {
    font-size: 16px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e6e6e6;
  margin: 0.25rem 2rem;
  width: calc(100% - 4rem);
`;

interface ShareArticleProps {
  title: string;
  url: string;
  summary?: string;
}

const ShareArticle: React.FC<ShareArticleProps> = ({ title, url, summary }) => {
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const handleShareClick = () => {
    if (navigator && 'share' in navigator) {
      const shareData =
        summary && summary !== title ? { title, text: summary, url } : { title, url };

      const canShareContent = !('canShare' in navigator) || navigator.canShare(shareData);

      if (canShareContent) {
        navigator
          .share(shareData)
          .then(() => {
            console.log('Content shared successfully');
          })
          .catch(err => {
            console.error('Share error:', err);

            // Don't show popover if user simply canceled the share dialog
            // The DOMException with name="AbortError" is thrown when user cancels
            if (!(err instanceof DOMException && err.name === 'AbortError')) {
              setShowPopover(true);
            }
          });
        return;
      }
    }

    setShowPopover(true);
  };

  const copyLink = () => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        console.log('URL copied to clipboard');
        setShowPopover(false);
      })
      .catch(err => console.error('Error copying text: ', err));
  };

  const shareViaFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank',
    );
    setShowPopover(false);
  };

  const shareViaX = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(
        url,
      )}`,
      '_blank',
    );
    setShowPopover(false);
  };

  const shareViaLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank',
    );
    setShowPopover(false);
  };

  const shareViaEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
      `${summary || title}\n\n${url}`,
    )}`;
    setShowPopover(false);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopover]);

  useEffect(() => {
    if (showPopover) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
    return () => {};
  }, [showPopover]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button ref={buttonRef} type="button" className={shareButtonStyle} onClick={handleShareClick}>
        <Icon path={mdiExportVariant} size={0.8} />
        <span>{t('Share this article')}</span>
      </button>

      {showPopover && (
        <SharePopover ref={popoverRef}>
          <TrianglePointer>
            <svg viewBox="0 0 21.4 10.7" width="20" height="10" transform="translate(0 -1.5)">
              <g id="pointerBackground">
                <polygon
                  points="1.2 .5 10.7 10 20.2 .5 1.2 .5"
                  style={{ stroke: 'none', strokeMiterlimit: 10, fill: 'white' }}
                />
              </g>
              <g id="blackStroke">
                <polyline
                  points="19 1.2 19.5 1.2 10.7 10 1.9 1.2 2.3 1.2"
                  style={{ fill: 'none', stroke: 'rgb(213 213 213)', strokeMiterlimit: 10 }}
                />
              </g>
            </svg>
          </TrianglePointer>

          <ShareOption onClick={copyLink}>
            <Icon path={mdiContentCopy} size={0.8} />
            <span>{t('Copy Link')}</span>
          </ShareOption>

          <Divider />

          <ShareOption onClick={shareViaFacebook}>
            <Icon path={mdiFacebook} size={0.8} color="#4267B2" />
            <span>{t('Share on Facebook')}</span>
          </ShareOption>

          <ShareOption onClick={shareViaX}>
            <svg width="16" height="16" viewBox="0 0 22 20" fill="none">
              <path d={xLogoPath} fill="#000000" />
            </svg>
            <span>{t('Share on X')}</span>
          </ShareOption>

          <ShareOption onClick={shareViaLinkedIn}>
            <Icon path={mdiLinkedin} size={0.8} color="#0077B5" />
            <span>{t('Share on LinkedIn')}</span>
          </ShareOption>

          <ShareOption onClick={shareViaEmail}>
            <Icon path={mdiEmailOutline} size={0.8} />
            <span>{t('Share with Email')}</span>
          </ShareOption>
        </SharePopover>
      )}
    </div>
  );
};

export default ShareArticle;
